import { firestore, auth } from '../firebase';
import { doc, setDoc, getDocs, getDoc, query, where, collection, addDoc, updateDoc, orderBy, limit } from 'firebase/firestore';
import { fetchBooks } from '../utils/bookService';
import { rateLimiter } from './simpleRateLimiter';
import { showErrorToast } from './toastUtils';

// Global rate limit check
const checkRateLimit = (userId) => {
    if (!rateLimiter.canMakeRequest(userId)) {
        throw new Error("Too many requests. Please wait a moment before trying again.");
    }
};

export const loadBingoGrid = async (gridId) => {
    const gridRef = doc(firestore, "BingoGrid", gridId);
    const gridDoc = await getDoc(gridRef);

    if (!gridDoc.exists()) throw new Error("Grid not found!");

    const cellQuery = query(collection(firestore, "BingoCell"), where("Grid_ID", "==", gridId));
    const cellSnapshot = await getDocs(cellQuery);

    const cells = cellSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
    }));

    return { title: gridDoc.data().Title, cells };
};

export const loadUserProgress = async () => {
    const user = auth.currentUser;
    if (!user) {
        return [];
    }

    try {
        // Check rate limit before any operations
        checkRateLimit(user.uid);

        const progressQuery = query(
            collection(firestore, "UserProgress"),
            where("User_ID", "==", user.uid)
        );
        
        const progressSnapshot = await getDocs(progressQuery);
        
        if (progressSnapshot.empty) {
            return [];
        }

        return progressSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        if (error.code === 'permission-denied') {
            showErrorToast("Permission denied. Please check your access rights.");
        }
        return [];
    }
};

export const fetchBookDetails = async (bookId) => {
    const user = auth.currentUser;
    if (user) {
        checkRateLimit(user.uid);
    }

    const bookRef = doc(firestore, "Book", bookId);
    const bookDoc = await getDoc(bookRef);
    return bookDoc.exists() ? { id: bookId, ...bookDoc.data() } : null;
};

export const saveFinishedBook = async (userId, bookId, bookTitle) => {
    // Check rate limit first
    if (!rateLimiter.canMakeRequest(userId)) {
        throw new Error("Too many book updates. Please wait a moment before trying again.");
    }

    const finishedBookId = `${userId}_${bookId}`;
    const finishedBookRef = doc(firestore, 'FinishedBook', finishedBookId);
    const finishedBookDoc = await getDoc(finishedBookRef);

    if (finishedBookDoc.exists()) {
        return { success: true, message: 'You have already finished this book!' };
    }

    // Get the book's cover URL from the Book collection
    const bookRef = doc(firestore, "Book", bookId);
    const bookDoc = await getDoc(bookRef);
    const coverUrl = bookDoc.exists() ? bookDoc.data().Cover_image : '';

    await setDoc(finishedBookRef, {
        User_ID: userId,
        Book_ID: bookId,
        Book_Title: bookTitle,
        Rating: null,
        Finished_date: new Date(),
        Cover_URL: coverUrl
    });

    return { success: true };
};

export const updateBookRating = async (userId, bookId, rating, review = '') => {
    if (!rateLimiter.canMakeRequest(userId)) {
        throw new Error("Too many rating updates. Please wait a moment before trying again.");
    }

    try {
        // Find the book document using a query
        const finishedBooksQuery = query(
            collection(firestore, "FinishedBook"),
            where("User_ID", "==", userId),
            where("Book_ID", "==", bookId)
        );
        const finishedBooksSnapshot = await getDocs(finishedBooksQuery);
        
        if (finishedBooksSnapshot.empty) {
            throw new Error("Book not found in your finished books list.");
        }

        // Update the first matching document
        const docRef = finishedBooksSnapshot.docs[0].ref;
        await updateDoc(docRef, {
            Rating: rating,
            Review: review,
            Rating_Date: new Date()
        });
        return { success: true };
    } catch (error) {
        if (error.code === 'not-found') {
            throw new Error("Book not found in your finished books list.");
        }
        throw error;
    }
};

export const fetchBookPage = async (bookId) => {
    const bookRef = doc(firestore, "Book", bookId);
    const bookDoc = await getDoc(bookRef);

    if (bookDoc.exists()) {
        const firestoreBook = bookDoc.data();
        return {
            id: bookId,
            title: firestoreBook.title || "No title available",
            authors: firestoreBook.authors || ["Unknown Author"],
            description: firestoreBook.description || "No description available",
            imageLinks: firestoreBook.imageLinks || {},
            industryIdentifiers: firestoreBook.industryIdentifiers || []
        };
    } else {
        // If not found in Firestore, fetch from Google Books API
        const books = await fetchBooks(bookId);

        if (books.length > 0) {
            const googleBookDetails = books[0].volumeInfo;
            return {
                id: bookId,
                title: googleBookDetails.title,
                authors: googleBookDetails.authors || ["Unknown Author"],
                description: googleBookDetails.description || "No description available",
                imageLinks: googleBookDetails.imageLinks || {},
                industryIdentifiers: googleBookDetails.industryIdentifiers || []
            };
        } else {
            return null;
        }
    }
};

export const updateFriendPointsCache = async (userId, newPoints) => {
    try {
        // Update points in sent friend requests
        const sentFriendsQuery = query(
            collection(firestore, "Friends"),
            where("sender", "==", userId)
        );
        const sentFriendsSnapshot = await getDocs(sentFriendsQuery);
        
        const sentUpdates = sentFriendsSnapshot.docs.map(doc => 
            updateDoc(doc.ref, { senderPoints: newPoints })
        );

        // Update points in received friend requests
        const receivedFriendsQuery = query(
            collection(firestore, "Friends"),
            where("receiver", "==", userId)
        );
        const receivedFriendsSnapshot = await getDocs(receivedFriendsQuery);
        
        const receivedUpdates = receivedFriendsSnapshot.docs.map(doc => 
            updateDoc(doc.ref, { receiverPoints: newPoints })
        );

        // Execute all updates
        await Promise.all([...sentUpdates, ...receivedUpdates]);
    } catch (error) {
        showErrorToast("Failed to update friend points");
    }
};

export const saveUserProgress = async (cellId, bookId, bookTitle) => {
    if (!auth.currentUser) throw new Error("You must be logged in to save progress.");

    const userId = auth.currentUser.uid;

    // Check rate limit first
    if (!rateLimiter.canMakeRequest(userId)) {
        throw new Error("Too many progress updates. Please wait a moment before trying again.");
    }

    const newProgressRef = await addDoc(collection(firestore, 'UserProgress'), {
        User_ID: userId,
        Cell_ID: cellId,
        Book_ID: bookId,
        Book_Title: bookTitle,
        Timestamp: new Date(),
    });

    return newProgressRef.id;
};

export const checkSelectedBookNeedsToBeSaved = async (book, bookId, userId) => {
    // Check rate limit first
    if (!rateLimiter.canMakeRequest(userId)) {
        throw new Error("Too many book updates. Please wait a moment before trying again.");
    }

    //1: Check if the book already exists in the "Book" collection
    const bookCollection = collection(firestore, 'Book');
    const bookQuery = query(bookCollection, where('ISBN', '==', bookId));
    const querySnapshot = await getDocs(bookQuery);

    if (!querySnapshot.empty) {
        // If book exists, use its ID
        const existingBook = querySnapshot.docs[0];
        bookId = existingBook.id;
    } else {
        // Add the book to the "Book" table if it doesn't exist
        const newBookRef = await addDoc(collection(firestore, 'Book'), {
            Title: book.volumeInfo.title,
            ISBN: bookId,
            Author: book.volumeInfo.authors?.join(', ') || 'Unknown',
            Genre: book.volumeInfo.categories?.[0] || 'General',
            Cover_image: book.volumeInfo.imageLinks?.thumbnail || '',
        });
        bookId = newBookRef.id;
    }
    return bookId;
}

export const fetchUserBooks = async (userId, type = 'finished') => {
    try {
        let querySnapshot;
        if (type === 'finished') {
            const finishedBooksQuery = query(
                collection(firestore, "FinishedBook"),
                where("User_ID", "==", userId)
            );
            querySnapshot = await getDocs(finishedBooksQuery);
        } else {
            const progressQuery = query(
                collection(firestore, "UserProgress"),
                where("User_ID", "==", userId)
            );
            querySnapshot = await getDocs(progressQuery);
        }

        const bookIds = querySnapshot.docs.map(doc => doc.data().Book_ID);
        const bookPromises = bookIds.map(async (bookId) => {
            const bookDoc = await getDoc(doc(firestore, "Book", bookId));
            return bookDoc.exists() ? { id: bookId, ...bookDoc.data() } : null;
        });
        
        return (await Promise.all(bookPromises)).filter(Boolean);
    } catch (error) {
        showErrorToast(`Failed to fetch ${type} books`);
        return [];
    }
};

export const fetchFriendBookActivities = async (userId) => {
    try {
        // Get user's friends
        const friendsQuery = query(
            collection(firestore, "Friends"),
            where("status", "==", "accepted"),
            where("sender", "==", userId)
        );
        const receivedFriendsQuery = query(
            collection(firestore, "Friends"),
            where("status", "==", "accepted"),
            where("receiver", "==", userId)
        );

        const [sentFriends, receivedFriends] = await Promise.all([
            getDocs(friendsQuery),
            getDocs(receivedFriendsQuery)
        ]);

        // Combine friend IDs
        const friendIds = [
            ...sentFriends.docs.map(doc => doc.data().receiver),
            ...receivedFriends.docs.map(doc => doc.data().sender)
        ];

        if (friendIds.length === 0) {
            return [];
        }

        // Get recent book activities from friends
        const activitiesQuery = query(
            collection(firestore, "FinishedBook"),
            where("User_ID", "in", friendIds),
            orderBy("Finished_date", "desc"),
            limit(20)
        );

        const activitiesSnapshot = await getDocs(activitiesQuery);
        
        // Get user details for each activity
        const activities = await Promise.all(
            activitiesSnapshot.docs.map(async (doc) => {
                const activity = doc.data();
                const userRef = doc(firestore, "users", activity.User_ID);
                const userDoc = await getDoc(userRef);
                const userData = userDoc.exists() ? userDoc.data() : null;

                return {
                    id: doc.id,
                    ...activity,
                    username: userData?.username || 'Unknown User',
                    profilePicture: userData?.profilePicture || 'https://ui-avatars.com/api/?name=User&background=random',
                    Finished_date: activity.Finished_date?.toDate(),
                    Rating_Date: activity.Rating_Date?.toDate()
                };
            })
        );

        return activities;
    } catch (error) {
        console.error("Error fetching friend activities:", error);
        throw new Error("Failed to fetch friend activities");
    }
};

